import { z } from 'zod';
export type AnyFilter = Record<string, unknown>;

export type BaseFilterOption<T extends AnyFilter = AnyFilter> = {
    id: Extract<keyof T, string>;
    label: string;
};

export type CheckboxFilterOption<T extends AnyFilter = AnyFilter> = {
    type: 'checkbox';
    options: {
        id: string;
        label: string;
        value: string;
        count?: number;
    }[];
    selectedValues?: string[];
    multiple?: boolean;
    onSearchOptions?: (searchTerm: string) => void;
    onSelectOption?: (value: string) => void;
    isLoadingOptions?: boolean;
} & BaseFilterOption<T>;

export type RadioFilterOption<T extends AnyFilter = AnyFilter> = {
    type: 'radio';
    options: {
        id: string;
        label: string;
        value: string;
    }[];
    selectedValue?: string | null;
} & BaseFilterOption<T>;

// Improved date range types
export const RelativeTimeUnit = {
    DAYS: 'days',
    WEEKS: 'weeks',
    MONTHS: 'months',
} as const;

export type RelativeTimeUnit = (typeof RelativeTimeUnit)[keyof typeof RelativeTimeUnit];

// Mapping of time units to SQL interval units
export const TIME_UNIT_TO_SQL_INTERVAL = {
    [RelativeTimeUnit.DAYS]: 'DAY',
    [RelativeTimeUnit.WEEKS]: 'WEEK',
    [RelativeTimeUnit.MONTHS]: 'MONTH',
} as const;

// Reusable Zod schema describing an entry date range which can be absolute (from/to)
// or relative (unit + count). It's optional and may be null.
// Comparison enum for relative ranges
export const DateRangeComparison = {
    NEWER: 'newer',
    OLDER: 'older',
} as const;

export type DateRangeComparison = (typeof DateRangeComparison)[keyof typeof DateRangeComparison];

// Accepts a Date (superjson clients) or an ISO string (the AI-chat tool layer);
// always outputs a Date. Same pattern as backwardCompatibleDate in explorer-utils.
const dateOrIsoString = z.union([
    z.date(),
    z.iso.datetime().transform((value) => new Date(value)),
    z.iso.date().transform((value) => new Date(value)),
]);

export const entryDateRangeSchema = z
    .object({
        from: dateOrIsoString.optional().describe('Start date (inclusive), ISO 8601.'),
        to: dateOrIsoString.optional().describe('End date (inclusive), ISO 8601.'),
        unit: z
            .enum(RelativeTimeUnit)
            .optional()
            .describe('Relative range unit; combine with count and comparison instead of from/to.'),
        comparison: z
            .enum(DateRangeComparison)
            .optional()
            .describe("'newer' = within the last count units, 'older' = before that."),
        count: z.number().optional().describe('Number of units for the relative range.'),
    })
    .optional()
    .nullable();

export type EntryDateRange = z.infer<typeof entryDateRangeSchema>;

// Map DateRangeComparison to a simple SQL operator string so callers can use a
// single lookup instead of branching logic.
export const DATE_RANGE_COMPARISON_TO_OPERATOR: Record<DateRangeComparison, '<' | '>='> = {
    [DateRangeComparison.OLDER]: '<',
    [DateRangeComparison.NEWER]: '>=',
};

export type DateRangeValue = {
    from?: Date;
    to?: Date;
};

export type RelativeDateRange = {
    unit: RelativeTimeUnit;
    count: number;
    /**
     * Optional comparison for relative ranges. 'newer' means "in the last X units",
     * 'older' means "older than X units". If omitted, callers should assume DateRangeComparison.NEWER.
     */
    comparison?: DateRangeComparison;
};

export type DateFilterValue = DateRangeValue | RelativeDateRange | null;

export type DateRangeFilterOption<T extends AnyFilter = AnyFilter> = {
    type: 'dateRange';
    selectedRange?: DateFilterValue;
    presetOptions?: {
        id: string;
        label: string;
        value: string;
    }[];
} & BaseFilterOption<T>;

export type PriceRangeFilterOption<T extends AnyFilter = AnyFilter> = {
    type: 'priceRange';
    selectedRange?: {
        min?: number;
        max?: number;
    } | null;
    currency?: string;
    minValue?: number;
    maxValue?: number;
} & BaseFilterOption<T>;

export type NumberRangeFilterOption<T extends AnyFilter = AnyFilter> = {
    type: 'numberRange';
    selectedRange?: {
        min?: number;
        max?: number;
    } | null;
    unit?: string;
    minValue?: number;
    maxValue?: number;
} & BaseFilterOption<T>;

export type FilterOptionWithValue<T extends AnyFilter = AnyFilter> =
    | CheckboxFilterOption<T>
    | RadioFilterOption<T>
    | DateRangeFilterOption<T>
    | PriceRangeFilterOption<T>
    | NumberRangeFilterOption<T>;

export type VerticalFilterOption<T extends AnyFilter = AnyFilter> =
    | (CheckboxFilterOption<T> & { selectedValues?: never })
    | (RadioFilterOption<T> & { selectedValue?: never })
    | (DateRangeFilterOption<T> & { selectedRange?: never })
    | (PriceRangeFilterOption<T> & { selectedRange?: never })
    | (NumberRangeFilterOption<T> & { selectedRange?: never });
